import { Optional } from '..';
import { BaseQueryParams } from '../../Global.model';
import { InvoiceService, InvoiceServiceUnit } from '../invoiceService/invoiceService.model';
import { AttachmentFile } from '../otherExpenseAttachment/otherExpenseAttachment.model';

export interface EiaSeries {
  id: string;
  label: string;
  sort_order: number;
}

export const EIA_SERIES: EiaSeries[] = [
  { id: 'EMD_EPD2D_PTE_NUS_DPG', label: 'U.S', sort_order: 1 },
  { id: 'EMD_EPD2D_PTE_R10_DPG', label: 'East Coast (PADD 1)', sort_order: 2 },
  { id: 'EMD_EPD2D_PTE_R1X_DPG', label: 'New England (PADD 1A)', sort_order: 3 },
  { id: 'EMD_EPD2D_PTE_R1Y_DPG', label: 'Central Atlantic (PADD 1B)', sort_order: 4 },
  { id: 'EMD_EPD2D_PTE_R1Z_DPG', label: 'Lower Atlantic (PADD 1C)', sort_order: 5 },
  { id: 'EMD_EPD2D_PTE_R20_DPG', label: 'Midwest (PADD 2)', sort_order: 6 },
  { id: 'EMD_EPD2D_PTE_R30_DPG', label: 'Gulf Coast (PADD 3)', sort_order: 7 },
  { id: 'EMD_EPD2D_PTE_R40_DPG', label: 'Rocky Mountain (PADD 4)', sort_order: 8 },
  { id: 'EMD_EPD2D_PTE_R50_DPG', label: 'West Coast (PADD 5)', sort_order: 9 },
  { id: 'EMD_EPD2D_PTE_R5XCA_DPG', label: 'West Coast less California', sort_order: 10 },
  { id: 'EMD_EPD2D_PTE_SCA_DPG', label: 'California', sort_order: 11 }
];

export enum FuelPriceType {
  EIA = 'EIA',
  CUSTOM = 'CUSTOM'
}

export enum Frequency {
  MONTHLY = 'monthly',
  WEEKLY = 'weekly',
  DAILY = 'daily'
}

export interface FuelSurchargeTableAttributes {
  id: string;
  name: string;
  fuel_price_type: FuelPriceType;
  eia_area_id: string | null;
  eia_area_name: string | null;
  price: number | null;
  period_date: Date | null;
  invoice_service_id: string | null;
  qbo_service_id: string | null;
  unit: InvoiceServiceUnit;
  document_id: string | null;
  note: string | null;
  tenant_id: string;
  qbo_attributes: Record<string, unknown> | null;
  division_id: string;
  created_at: Date;
  updated_at: Date;
  deleted_at: Date | null;

  // Relationships
  fuel_surcharge_table_prices?: FuelSurchargeTablePriceAttributes[];
  fuel_surcharge_rate_ranges?: FuelSurchargeRateRangeAttributes[];
  invoice_service?: InvoiceService | null;
}
export type FuelSurchargeTableTimestamps = 'created_at' | 'updated_at' | 'deleted_at';
export type FuelSurchargeTableNonCreationalOrEditableAttributes =
  | 'id'
  | 'tenant_id'
  | 'qbo_attributes'
  | FuelSurchargeTableTimestamps;
export type FuelSurchargeTableNullableAttributes =
  | 'eia_area_id'
  | 'eia_area_name'
  | 'price'
  | 'period_date'
  | 'invoice_service_id'
  | 'qbo_service_id'
  | 'document_id'
  | 'qbo_attributes'
  | 'note';
export type FuelSurchargeTableCreationAttributes = Omit<
  Optional<FuelSurchargeTableAttributes, FuelSurchargeTableNullableAttributes>,
  FuelSurchargeTableNonCreationalOrEditableAttributes
>;
export type FuelSurchargeTableUpdationAttributes = Partial<FuelSurchargeTableCreationAttributes>;

export type PriceBuilderRow = Omit<
  Optional<FuelSurchargeTablePriceCreationAttributes, 'price'>,
  'fuel_surcharge_table_id' | 'start_date' | 'end_date'
> & {
  id?: string;
  start_date: string | Date | null;
  end_date: string | Date | null;
  sort_order: number;
};

export type RateRangeBuilderRow = Omit<
  Optional<FuelSurchargeRateRangeCreationAttributes, 'range_from' | 'range_to' | 'rate'>,
  'fuel_surcharge_table_id' | 'tenant_id'
> & {
  id?: string;
  sort_order: number;
};

// Fuel Surcharge Table request parameters
export type CreateFuelSurchargeTableRequest = FuelSurchargeTableCreationAttributes & {
  prices: PriceBuilderRow[];
  rate_ranges: RateRangeBuilderRow[];
};
export type UpdateFuelSurchargeTableRequest = FuelSurchargeTableUpdationAttributes & {
  id: string;
  prices: (Omit<FuelSurchargeTablePriceCreationAttributes, 'fuel_surcharge_table_id'> & {
    id?: string;
  })[];
  rate_ranges: (Omit<FuelSurchargeRateRangeCreationAttributes, 'fuel_surcharge_table_id'> & {
    id?: string;
  })[];
  prices_to_delete: string[];
  rate_ranges_to_delete: string[];
  is_document_deleted?: boolean;
};
export type SearchFuelSurchargeTableFilters = {
  fuel_price_source?: string;
  division_id?: string;
};
export type SearchFuelSurchargeTableRequest = {
  filters: SearchFuelSurchargeTableFilters;
  params?: BaseQueryParams;
};

export type GetDieselPriceDataRequest = {
  filters: {
    frequency: Frequency;
    serieId: string;
    startDate?: string;
    endDate?: string;
    numberOfPeriods?: number;
  };
};

export type UploadFuelSurchargeTableDocumentRequest = {
  file: File;
  fuelSurchargeTableId: string;
};

// Fuel Surcharge Table Price
export interface FuelSurchargeTablePriceAttributes {
  id: string;
  fuel_surcharge_table_id: string;
  price: number;
  start_date: Date;
  end_date: Date;
  tenant_id: string;
  created_at: Date;
  updated_at: Date;
  deleted_at: Date | null;
  sort_order: number;

  // Relationships
  fuel_surcharge_table?: FuelSurchargeTableAttributes;
}
export type FuelSurchargeTablePriceNullableAttributes = 'start_date' | 'end_date';
export type FuelSurchargeTablePriceTimestamps = 'created_at' | 'updated_at' | 'deleted_at';
export type FuelSurchargeTablePriceNonCreationalOrEditableAttributes =
  | 'id'
  | 'tenant_id'
  | FuelSurchargeTablePriceTimestamps;
export type FuelSurchargeTablePriceCreationAttributes = Omit<
  Optional<FuelSurchargeTablePriceAttributes, FuelSurchargeTablePriceNullableAttributes>,
  FuelSurchargeTablePriceNonCreationalOrEditableAttributes
>;

// Fuel Surcharge Rate Range
export interface FuelSurchargeRateRangeAttributes {
  id: string;
  fuel_surcharge_table_id: string;
  range_from: number;
  range_to: number;
  rate: number;
  tenant_id: string;
  created_at: Date;
  updated_at: Date;
  deleted_at: Date | null;
  sort_order: number;
  // Relationships
  fuel_surcharge_table?: FuelSurchargeTableAttributes;
}
export type FuelSurchargeRateRangeTableTimestamps = 'created_at' | 'updated_at' | 'deleted_at';
export type FuelSurchargeRateRangeNonCreationalOrEditableAttributes =
  | 'id'
  | 'tenant_id'
  | FuelSurchargeRateRangeTableTimestamps;
export type FuelSurchargeRateRangeNullableAttributes = 'deleted_at';
export type FuelSurchargeRateRangeCreationAttributes = Omit<
  Optional<FuelSurchargeRateRangeAttributes, FuelSurchargeRateRangeNullableAttributes>,
  FuelSurchargeRateRangeNonCreationalOrEditableAttributes
>;

// responses
export type SearchFuelSurchargeTableResponseDto = Omit<
  FuelSurchargeTableAttributes,
  FuelSurchargeTableTimestamps
> & {
  prices: Pick<FuelSurchargeTablePriceAttributes, 'id' | 'price' | 'start_date' | 'end_date'>[];
  rate_ranges: Pick<FuelSurchargeRateRangeAttributes, 'id' | 'rate' | 'range_from' | 'range_to'>[];
};

export type GetAllFuelSurchargeTablesResponseDto = Omit<
  FuelSurchargeTableAttributes,
  FuelSurchargeTableTimestamps
>;

export type CreateFuelSurchargeTableResponseDto = Omit<
  FuelSurchargeTableAttributes,
  FuelSurchargeTableTimestamps
>;
export type UpdateFuelSurchargeTableResponseDto = Omit<
  FuelSurchargeTableAttributes,
  FuelSurchargeTableTimestamps
>;
export type UploadFuelSurchargeTableDocumentResponseDto = CreateFuelSurchargeTableResponseDto;

export type GetEiaDieselPriceDataResponseDto = {
  period: string;
  duoarea: string;
  area_name: string;
  product: string;
  product_name: string;
  serie_id: string;
  serie_description: string;
  value: number;
  units: string;
};

export type FuelSurchargeTableFormikDto = Omit<CreateFuelSurchargeTableRequest, 'unit'> & {
  unit: string;
  prices_to_delete: string[];
  rate_ranges_to_delete: string[];
  is_document_deleted: boolean;
};

export interface FuelSurchargeTableDocumentResponseDto {
  id: string;
  name: string;
  size: number;
  key: string;
  file_type: string;
  s3_signed_url?: string;
  s3_signed_download_url?: string;
}

export type GetFuelSurchargeTableResponseDto = Omit<
  FuelSurchargeTableAttributes,
  FuelSurchargeTableTimestamps
> & {
  prices: Omit<FuelSurchargeTablePriceAttributes, FuelSurchargeTablePriceTimestamps>[];
  rate_ranges: Omit<FuelSurchargeRateRangeAttributes, FuelSurchargeTableTimestamps>[];
  document: AttachmentFile | null;
};
